'use client'

import { useState } from 'react'
import { Button, Divider, Tabs } from 'antd'
import type { TabsProps } from 'antd'
import { useRouter } from 'next/navigation'
import { useTranslation } from 'react-i18next'

import useSWR from 'swr'
import ResourceOverview from './components/ResourceOverview'
import InstanceDisplayList from './components/InstanceDisplayList'
import TrainingTaskDisplayList from './components/TrainingTaskDisplayList'
import DeploymentDisplayList from './components/DeploymentDisplayList'
import BillingCenter from './components/BillingCenter'
import { getWebsiteTitleConfig } from '@/service/config'
import { useConfigContext } from '@/context/config-context'
import { useBillingEnabled } from '@/app/components/base/billing-guard'
import { isLTC } from '@/config'

import HERO_BG from '@/fe-common/assets/hero-pc.png'

const App = () => {
  const [activeKey, setActiveKey] = useState('1')
  const router = useRouter()
  const { t } = useTranslation()
  const config = useConfigContext()
  // 获取网站 title
  const { data: websiteTitle } = useSWR(
    '/api/website/title',
    getWebsiteTitleConfig,
  )

  const handleMoreDataClick = () => {
    // 根据当前 active tab 跳转到对应路由
    switch (activeKey) {
      case '1':
        router.push('/ecs/instances')
        break
      case '2':
        router.push('/train/tasks')
        break
      case '3':
        router.push('/deploy/deployments')
        break
      default:
        break
    }
  }

  const items = [
    config.mlops.ecs.enabled && {
      key: '1',
      label: t('ecs.userPage.containerInstanceList'),
      children: <InstanceDisplayList />,
    },
    config.mlops.train.enabled && {
      key: '2',
      label: t('ecs.userPage.trainingTaskList'),
      children: <TrainingTaskDisplayList />,
    },
    config.mlops.deploy.enabled && {
      key: '3',
      label: t('ecs.userPage.inferenceServiceList'),
      children: <DeploymentDisplayList />,
    },
  ].filter(Boolean) as TabsProps['items']

  return (
    <div>
      <div
        className='px-6 py-4 rounded-lg border-b border-gray-200 bg-[#EFF8FF] mb-4'
        style={{
          backgroundImage: `url(${HERO_BG.src})`,
          backgroundPosition: 'right center',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
        }}>
        <h1 className='mb-2 text-2xl font-medium text-gray-900'>
          {t('ecs.userPage.welcomeTitle', { name: websiteTitle?.value ?? '--' })}
        </h1>
        <p className='text-base leading-relaxed text-gray-500'>
          {t('ecs.userPage.description')}
        </p>
      </div>
      <main className='flex gap-4'>
        <section 
          className='flex-1 space-y-4' 
          style={{ 
            maxWidth: (useBillingEnabled() || isLTC) ? 'calc(100% - 400px)' : '100%' 
          }}>
          <ResourceOverview />
          <Divider />
          <div>
            <Tabs
              items={items}
              defaultActiveKey='1'
              activeKey={activeKey}
              onChange={setActiveKey}
              tabBarExtraContent={
                <Button type='link' onClick={handleMoreDataClick}>
                  {t('ecs.userPage.moreData')}
                </Button>
              }
            />
          </div>
        </section>
        {(useBillingEnabled() || isLTC) && <BillingCenter />}
      </main>
    </div>
  )
}

export default App
